



















import React, { useState, useEffect, useMemo } from "react";
import {
  History,
  Search,
  Download,
  Filter,
  Eye,
  ChevronRight,
  Calendar,
  LayoutGrid,
  List,
  ArrowUpDown,
  CheckCircle2,
  Clock,
  AlertCircle,
  FileText,
  Banknote
} from "lucide-react";
import { getPayrollRecordsApi, approvePayrollApi, markPayrollPaidApi, exportPayrollReportApi, getPayslipApi } from "../../app/api";
import PayrollKPIs from "./components/PayrollKPIs";
import PayslipModal from "./components/PayslipModal";
import { toast } from "react-hot-toast";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";

const PayrollRecords = () => {
  const previousMonth = moment().subtract(1, 'month').format("YYYY-MM");
  const [fromMonth, setFromMonth] = useState(previousMonth); // YYYY-MM
  const [toMonth, setToMonth] = useState(previousMonth); // YYYY-MM
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [viewType, setViewType] = useState("list");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: 'NAME', direction: 'asc' });
  const [isApproving, setIsApproving] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [payslipData, setPayslipData] = useState(null);
  const [isPayslipModalOpen, setIsPayslipModalOpen] = useState(false);
  const [isFetchingPayslip, setIsFetchingPayslip] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;

  useEffect(() => {
    fetchRecords();
  }, [fromMonth, toMonth, refreshTrigger]);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const res = await getPayrollRecordsApi(fromMonth, toMonth);
      if (res.success) {
        setRecords(res.data);
      } else {
        toast.error(res.message || "Failed to fetch payroll records");
      }
    } catch (error) {
      console.error("Error fetching records:", error);
      toast.error("An error occurred while fetching records");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      setIsApproving(true);
      const res = await approvePayrollApi({ month: fromMonth });
      if (res.success) {
        toast.success("Payroll approved successfully!");
        setRefreshTrigger(prev => prev + 1);
      }
    } catch (error) {
      toast.error("Failed to approve payroll");
    } finally {
      setIsApproving(false);
    }
  };

  const handlePaid = async () => {
    try {
      setIsPaying(true);
      const res = await markPayrollPaidApi({ month: fromMonth });
      if (res.success) {
        toast.success("Payroll marked as paid!");
        setRefreshTrigger(prev => prev + 1);
      }
    } catch (error) {
      toast.error("Failed to mark as paid");
    } finally {
      setIsPaying(false);
    }
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      await exportPayrollReportApi(fromMonth, toMonth);
      toast.success("Report downloaded successfully!");
    } catch (error) {
      toast.error("Failed to export report");
    } finally {
      setIsExporting(false);
    }
  };

  const handleViewPayslip = async (userId) => {
    try {
      setIsFetchingPayslip(true);

      // fromMonth is YYYY-MM (e.g. 2026-05)
      // API expects userId, month (name), year (string)
      const dateObj = moment(fromMonth, "YYYY-MM");
      const monthName = dateObj.format("MMMM"); // "May"
      const yearStr = dateObj.format("YYYY");   // "2026"

      const res = await getPayslipApi(userId, monthName, yearStr);
      if (res.success) {
        setPayslipData(res.data);
        setIsPayslipModalOpen(true);
      } else {
        toast.error("Failed to load payslip data");
      }
    } catch (error) {
      toast.error("Error loading payslip");
    } finally {
      setIsFetchingPayslip(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedRecords = [...records].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const filteredRecords = sortedRecords.filter(rec =>
    rec.NAME?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    rec.user_id?.toString().includes(searchTerm)
  );

  const processedRecords = useMemo(() => {
    if (fromMonth === toMonth) return filteredRecords;

    const grouped = new Map();
    filteredRecords.forEach(rec => {
      const key = rec.user_id;
      if (!grouped.has(key)) {
        grouped.set(key, { ...rec, allMonths: [rec.month] });
      } else {
        const existing = grouped.get(key);
        existing.gross_salary = Number(existing.gross_salary) + Number(rec.gross_salary);
        existing.total_deduction = Number(existing.total_deduction) + Number(rec.total_deduction);
        existing.hold_back_amount = Number(existing.hold_back_amount || 0) + Number(rec.hold_back_amount || 0);
        existing.net_salary = Number(existing.net_salary) + Number(rec.net_salary);
        if (!existing.allMonths.includes(rec.month)) {
          existing.allMonths.push(rec.month);
        }
        
        // Status priority: paid > approved > draft
        const statusOrder = { 'paid': 3, 'approved': 2, 'draft': 1 };
        if (statusOrder[rec.status] > statusOrder[existing.status]) {
          existing.status = rec.status;
        }
      }
    });

    return Array.from(grouped.values()).map(rec => {
      if (rec.allMonths.length > 1) {
        const sorted = [...rec.allMonths].sort();
        rec.month = `${sorted[0]} to ${sorted[sorted.length - 1]}`;
      }
      return rec;
    });
  }, [filteredRecords, fromMonth, toMonth]);

  const totalPages = Math.ceil(processedRecords.length / recordsPerPage) || 1;
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = processedRecords.slice(indexOfFirstRecord, indexOfLastRecord);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, fromMonth, toMonth]);

  const isPayrollApproved = records.some(r => r.status === "approved" || r.status === "paid");
  const isPayrollPaid = records.some(r => r.status === "paid");

  if (loading) {
    return (
      <div className="min-h-screen bg-[#12082F] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#12082F] text-white p-4 sm:p-6 font-sans">
      <div className="max-w-[1600px] mx-auto space-y-6">

        {/* HEADER */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2 text-white">
              PAYROLL RECORDS <span className="text-white/70 font-normal">| {moment(fromMonth).format("MMMM YYYY")}{fromMonth !== toMonth && toMonth && ` - ${moment(toMonth).format("MMMM YYYY")}`}</span>
            </h1>
            <p className="text-white/50 text-sm">Historical payroll data and processing logs</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-2 shadow-inner">
              <div className="flex items-center gap-3">
                <span className="text-[10px] text-white/40 uppercase font-black tracking-tighter">FROM</span>
                <DatePicker
                  selected={fromMonth ? new Date(fromMonth + "-01") : null}
                  onChange={(date) => {
                    setFromMonth(date ? moment(date).format("YYYY-MM") : "");
                  }}
                  dateFormat="MMMM, yyyy"
                  showMonthYearPicker
                  maxDate={moment().subtract(1, 'month').endOf('month').toDate()}
                  className="bg-transparent border-none text-sm text-white font-bold focus:outline-none cursor-pointer w-28 text-center"
                />
              </div>
              <div className="w-[1px] h-6 bg-white/10" />
              <div className="flex items-center gap-3">
                <span className="text-[10px] text-white/40 uppercase font-black tracking-tighter">TO</span>
                <DatePicker
                  selected={toMonth ? new Date(toMonth + "-01") : null}
                  onChange={(date) => {
                    setToMonth(date ? moment(date).format("YYYY-MM") : "");
                  }}
                  dateFormat="MMMM, yyyy"
                  showMonthYearPicker
                  maxDate={moment().subtract(1, 'month').endOf('month').toDate()}
                  className="bg-transparent border-none text-sm text-white font-bold focus:outline-none cursor-pointer w-28 text-center"
                />
              </div>
            </div>
            {records.length > 0 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleApprove}
                  disabled={isApproving || isPayrollApproved}
                  className="bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 border border-blue-500/30 px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition disabled:opacity-50"
                >
                  {isApproving ? <span className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" /> : <CheckCircle2 size={16} />}
                  Approve
                </button>
                <button
                  onClick={handlePaid}
                  disabled={isPaying || isPayrollPaid}
                  className="bg-green-600/20 hover:bg-green-600/30 text-green-400 border border-green-500/30 px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition disabled:opacity-50"
                >
                  {isPaying ? <span className="w-4 h-4 border-2 border-green-400 border-t-transparent rounded-full animate-spin" /> : <Banknote size={16} />}
                  Mark Paid
                </button>
              </div>
            )}
            <button
              onClick={handleExport}
              disabled={isExporting}
              className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition border border-white/10 disabled:opacity-50"
            >
              {isExporting ? <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <Download size={16} />}
              Export Report
            </button>
          </div>
        </div>

        {/* SUMMARY STATS */}
        <PayrollKPIs fromMonth={fromMonth} toMonth={toMonth} refreshTrigger={refreshTrigger} />

        {/* CONTROLS */}
        <div className="bg-white/5 border border-white/10 p-4 rounded-2xl flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="relative w-full md:w-96">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/60" />
            <input
              type="text"
              placeholder="Search by name or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-purple-500/50 transition"
            />
          </div>

          <div className="flex items-center gap-3 w-full md:w-auto">
            <div className="flex bg-white/5 border border-white/10 rounded-lg p-1">
              <button
                onClick={() => setViewType("list")}
                className={`p-2 rounded-md transition ${viewType === "list" ? "bg-purple-600 text-white" : "text-white/70 hover:text-white"}`}
              >
                <List size={18} />
              </button>
              <button
                onClick={() => setViewType("grid")}
                className={`p-2 rounded-md transition ${viewType === "grid" ? "bg-purple-600 text-white" : "text-white/70 hover:text-white"}`}
              >
                <LayoutGrid size={18} />
              </button>
            </div>
            <button className="flex-1 md:flex-none bg-white/5 border border-white/10 px-4 py-2.5 rounded-xl flex items-center justify-center gap-2 hover:bg-white/10 transition text-sm">
              <Filter size={18} /> Filters
            </button>
          </div>
        </div>

        {/* RECORDS LIST/GRID */}
        {viewType === "list" ? (
          <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[1000px]">
              <thead>
                <tr className="border-b border-white/10 bg-white/5">
                  <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider cursor-pointer group" onClick={() => handleSort('user_id')}>
                    <div className="flex items-center gap-2">ID <ArrowUpDown size={12} className="opacity-0 group-hover:opacity-100 transition" /></div>
                  </th>
                  <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider cursor-pointer group" onClick={() => handleSort('NAME')}>
                    <div className="flex items-center gap-2">Employee <ArrowUpDown size={12} className="opacity-0 group-hover:opacity-100 transition" /></div>
                  </th>
                  <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider text-right">Month</th>
                  <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider text-right">Gross Salary</th>
                  <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider text-right">Total Deductions</th>
                  <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider text-right">Hold-back</th>
                  <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider text-right">Net Salary</th>
                  <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider text-center">Status</th>
                  <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {currentRecords.map((rec) => (
                  <tr key={rec.id} className="hover:bg-white/5 transition group">
                    <td className="p-4 text-sm text-white/60">{rec.user_id}</td>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-[12px] font-bold uppercase">
                          {(rec.NAME || "U")[0]}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-white">{rec.NAME}</p>
                          {/* <p className="text-[10px] text-white/70">{rec.created_at}</p> */}
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-sm text-right text-white/60">{rec.month}</td>
                    <td className="p-4 text-sm font-medium text-right">₹{Number(rec.gross_salary).toLocaleString()}</td>
                    <td className="p-4 text-sm font-medium text-right text-red-400">₹{Number(rec.total_deduction).toLocaleString()}</td>
                    <td className="p-4 text-sm font-medium text-right text-amber-400">-₹{Number(rec.hold_back_amount || 0).toLocaleString()}</td>
                    <td className="p-4 text-right">
                      <span className="text-sm font-black text-green-400">₹{Number(rec.net_salary).toLocaleString()}</span>
                    </td>
                    <td className="p-4 text-center">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${rec.status === 'draft' ? 'bg-yellow-500/10 text-yellow-500 border border-yellow-500/20' :
                        rec.status === 'approved' ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' :
                          'bg-green-500/10 text-green-500 border border-green-500/20'
                        }`}>
                        {rec.status === 'draft' ? <Clock size={10} /> : <CheckCircle2 size={10} />}
                        {rec.status}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleViewPayslip(rec.user_id)}
                          disabled={isFetchingPayslip}
                          className="p-2 hover:bg-white/10 rounded-lg text-white/70 hover:text-white transition disabled:opacity-50"
                          title="View Payslip"
                        >
                          <FileText size={16} />
                        </button>
                        {/* <button className="p-2 hover:bg-white/10 rounded-lg text-white/70 hover:text-white transition" title="View Details">
                          <Eye size={16} />
                        </button> */}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {currentRecords.map((rec) => {
              const isMaturityMonth = Number(rec.net_salary) > (Number(rec.gross_salary) - Number(rec.total_deduction));
              return (
                <div key={rec.id} className={`bg-white/5 border border-white/10 p-5 rounded-2xl hover:border-purple-500/50 transition space-y-4 group relative ${isMaturityMonth ? 'ring-2 ring-purple-500 shadow-[0_0_20px_rgba(168,85,247,0.4)]' : ''}`}>
                  {isMaturityMonth && (
                    <div className="absolute -top-3 -right-3 bg-purple-500 text-white text-[10px] font-black px-3 py-1 rounded-full shadow-lg animate-bounce z-10">
                      MATURITY RELEASED!
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center font-bold border-2 border-white/10 uppercase">
                        {(rec.NAME || "U")[0]}
                      </div>
                      <div>
                        <h4 className="text-sm font-bold text-white">{rec.NAME}</h4>
                        <p className="text-[10px] text-white/70">ID: {rec.user_id}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest ${rec.status === 'draft' ? 'bg-yellow-500/20 text-yellow-500 border border-yellow-500/20' :
                      rec.status === 'approved' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/20' :
                        'bg-green-500/20 text-green-400 border border-green-500/20'
                      }`}>
                      {rec.status}
                    </span>
                  </div>

                  <div className="space-y-2 py-3 border-y border-white/5">
                    <DataRow label="Gross Salary" value={`₹${Number(rec.gross_salary).toLocaleString()}`} />
                    <DataRow label="Deductions" value={`-₹${Number(rec.total_deduction).toLocaleString()}`} color="text-red-400" />
                    <DataRow label="Hold-back" value={`-₹${Number(rec.hold_back_amount || 0).toLocaleString()}`} color="text-amber-400" />
                    {isMaturityMonth && (
                      <div className="flex justify-between text-[11px] bg-purple-500/10 p-1 rounded">
                        <span className="text-purple-300 font-bold">Released Amount</span>
                        <span className="font-black text-purple-300">+₹{(Number(rec.net_salary) - (Number(rec.gross_salary) - Number(rec.total_deduction) - Number(rec.hold_back_amount || 0))).toLocaleString()}</span>
                      </div>
                    )}
                    <DataRow label="Month" value={rec.month} />
                  </div>

                  <div className="flex items-center justify-between pt-1">
                    <div>
                      <p className="text-[10px] text-white/60 uppercase font-bold tracking-wider">Net Payable</p>
                      <p className="text-xl font-black text-green-400">₹{Number(rec.net_salary).toLocaleString()}</p>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleViewPayslip(rec.user_id)}
                        disabled={isFetchingPayslip}
                        className="bg-white/5 hover:bg-white/10 p-2.5 rounded-xl transition border border-white/5 disabled:opacity-50"
                        title="View Payslip"
                      >
                        <FileText size={16} className="text-white/60" />
                      </button>
                      {/* <button className="bg-white/5 hover:bg-white/10 p-2.5 rounded-xl transition border border-white/5">
                      <Eye size={16} className="text-white/60" />
                    </button> */}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* PAGINATION */}
        {filteredRecords.length > recordsPerPage && (
          <div className="flex items-center justify-center gap-2 py-6">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="p-2 text-white/70 hover:text-white transition disabled:opacity-30"
            >
              <ChevronRight size={18} className="rotate-180" />
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`w-8 h-8 rounded-lg text-sm font-bold transition ${currentPage === page
                  ? 'bg-purple-600 text-white'
                  : 'bg-white/5 text-white/60 hover:bg-white/10'
                  }`}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="p-2 text-white/70 hover:text-white transition disabled:opacity-30"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        )}

        {processedRecords.length === 0 && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-12 text-center">
            <div className="bg-white/5 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <History size={32} className="text-white/50" />
            </div>
            <h3 className="text-lg font-bold text-white">No payroll records found</h3>
            <p className="text-white/70 text-sm mt-1">Try selecting a different month or processing payroll first</p>
          </div>
        )}

        <PayslipModal
          isOpen={isPayslipModalOpen}
          onClose={() => setIsPayslipModalOpen(false)}
          data={payslipData}
        />
      </div>
    </div>
  );
};

const DataRow = ({ label, value, color = "text-white" }) => (
  <div className="flex justify-between items-center text-[11px]">
    <span className="text-white/70 font-medium">{label}</span>
    <span className={`${color} font-bold`}>{value}</span>
  </div>
);

export default PayrollRecords;














// import React, { useState, useEffect } from "react";
// import {
//   History,
//   Search,
//   Download,
//   Filter,
//   Eye,
//   ChevronRight,
//   Calendar,
//   LayoutGrid,
//   List,
//   ArrowUpDown,
//   CheckCircle2,
//   Clock,
//   AlertCircle,
//   FileText,
//   Banknote
// } from "lucide-react";
// import { getPayrollRecordsApi, approvePayrollApi, markPayrollPaidApi, exportPayrollReportApi, getPayslipApi } from "../../app/api";
// import PayrollKPIs from "./components/PayrollKPIs";
// import PayslipModal from "./components/PayslipModal";
// import { toast } from "react-hot-toast";
// import DatePicker from "react-datepicker";
// import "react-datepicker/dist/react-datepicker.css";
// import moment from "moment";

// const PayrollRecords = () => {
//   const previousMonth = moment().subtract(1, 'month').format("YYYY-MM");
//   const [fromMonth, setFromMonth] = useState(previousMonth); // YYYY-MM
//   const [toMonth, setToMonth] = useState(previousMonth); // YYYY-MM
//   const [loading, setLoading] = useState(true);
//   const [records, setRecords] = useState([]);
//   const [viewType, setViewType] = useState("list");
//   const [searchTerm, setSearchTerm] = useState("");
//   const [sortConfig, setSortConfig] = useState({ key: 'NAME', direction: 'asc' });
//   const [isApproving, setIsApproving] = useState(false);
//   const [isPaying, setIsPaying] = useState(false);
//   const [isExporting, setIsExporting] = useState(false);
//   const [refreshTrigger, setRefreshTrigger] = useState(0);

//   const [payslipData, setPayslipData] = useState(null);
//   const [isPayslipModalOpen, setIsPayslipModalOpen] = useState(false);
//   const [isFetchingPayslip, setIsFetchingPayslip] = useState(false);
//   const [currentPage, setCurrentPage] = useState(1);
//   const recordsPerPage = 10;

//   useEffect(() => {
//     fetchRecords();
//   }, [fromMonth, toMonth, refreshTrigger]);

//   const fetchRecords = async () => {
//     try {
//       setLoading(true);
//       const res = await getPayrollRecordsApi(fromMonth, toMonth);
//       if (res.success) {
//         setRecords(res.data);
//       } else {
//         toast.error(res.message || "Failed to fetch payroll records");
//       }
//     } catch (error) {
//       console.error("Error fetching records:", error);
//       toast.error("An error occurred while fetching records");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleApprove = async () => {
//     try {
//       setIsApproving(true);
//       const res = await approvePayrollApi({ month: fromMonth });
//       if (res.success) {
//         toast.success("Payroll approved successfully!");
//         setRefreshTrigger(prev => prev + 1);
//       }
//     } catch (error) {
//       toast.error("Failed to approve payroll");
//     } finally {
//       setIsApproving(false);
//     }
//   };

//   const handlePaid = async () => {
//     try {
//       setIsPaying(true);
//       const res = await markPayrollPaidApi({ month: fromMonth });
//       if (res.success) {
//         toast.success("Payroll marked as paid!");
//         setRefreshTrigger(prev => prev + 1);
//       }
//     } catch (error) {
//       toast.error("Failed to mark as paid");
//     } finally {
//       setIsPaying(false);
//     }
//   };

//   const handleExport = async () => {
//     try {
//       setIsExporting(true);
//       await exportPayrollReportApi(fromMonth, toMonth);
//       toast.success("Report downloaded successfully!");
//     } catch (error) {
//       toast.error("Failed to export report");
//     } finally {
//       setIsExporting(false);
//     }
//   };

//   const handleViewPayslip = async (userId) => {
//     try {
//       setIsFetchingPayslip(true);

//       // fromMonth is YYYY-MM (e.g. 2026-05)
//       // API expects userId, month (name), year (string)
//       const dateObj = moment(fromMonth, "YYYY-MM");
//       const monthName = dateObj.format("MMMM"); // "May"
//       const yearStr = dateObj.format("YYYY");   // "2026"

//       const res = await getPayslipApi(userId, monthName, yearStr);
//       if (res.success) {
//         setPayslipData(res.data);
//         setIsPayslipModalOpen(true);
//       } else {
//         toast.error("Failed to load payslip data");
//       }
//     } catch (error) {
//       toast.error("Error loading payslip");
//     } finally {
//       setIsFetchingPayslip(false);
//     }
//   };

//   const handleSort = (key) => {
//     let direction = 'asc';
//     if (sortConfig.key === key && sortConfig.direction === 'asc') {
//       direction = 'desc';
//     }
//     setSortConfig({ key, direction });
//   };

//   const sortedRecords = [...records].sort((a, b) => {
//     if (a[sortConfig.key] < b[sortConfig.key]) {
//       return sortConfig.direction === 'asc' ? -1 : 1;
//     }
//     if (a[sortConfig.key] > b[sortConfig.key]) {
//       return sortConfig.direction === 'asc' ? 1 : -1;
//     }
//     return 0;
//   });

//   const filteredRecords = sortedRecords.filter(rec =>
//     rec.NAME?.toLowerCase().includes(searchTerm.toLowerCase()) ||
//     rec.user_id?.toString().includes(searchTerm)
//   );

//   const totalPages = Math.ceil(filteredRecords.length / recordsPerPage) || 1;
//   const indexOfLastRecord = currentPage * recordsPerPage;
//   const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
//   const currentRecords = filteredRecords.slice(indexOfFirstRecord, indexOfLastRecord);

//   useEffect(() => {
//     setCurrentPage(1);
//   }, [searchTerm, fromMonth, toMonth]);

//   const isPayrollApproved = records.some(r => r.status === "approved" || r.status === "paid");
//   const isPayrollPaid = records.some(r => r.status === "paid");

//   if (loading) {
//     return (
//       <div className="min-h-screen bg-[#12082F] flex items-center justify-center">
//         <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
//       </div>
//     );
//   }

//   return (
//     <div className="min-h-screen bg-[#12082F] text-white p-4 sm:p-6 font-sans">
//       <div className="max-w-[1600px] mx-auto space-y-6">

//         {/* HEADER */}
//         <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
//           <div>
//             <h1 className="text-2xl font-bold flex items-center gap-2 text-white">
//               PAYROLL RECORDS <span className="text-white/70 font-normal">| {moment(fromMonth).format("MMMM YYYY")}{fromMonth !== toMonth && toMonth && ` - ${moment(toMonth).format("MMMM YYYY")}`}</span>
//             </h1>
//             <p className="text-white/50 text-sm">Historical payroll data and processing logs</p>
//           </div>
//           <div className="flex flex-wrap items-center gap-3">
//             <div className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-2 shadow-inner">
//               <div className="flex items-center gap-3">
//                 <span className="text-[10px] text-white/40 uppercase font-black tracking-tighter">FROM</span>
//                 <DatePicker
//                   selected={fromMonth ? new Date(fromMonth + "-01") : null}
//                   onChange={(date) => {
//                     setFromMonth(date ? moment(date).format("YYYY-MM") : "");
//                   }}
//                   dateFormat="MMMM, yyyy"
//                   showMonthYearPicker
//                   maxDate={moment().subtract(1, 'month').endOf('month').toDate()}
//                   className="bg-transparent border-none text-sm text-white font-bold focus:outline-none cursor-pointer w-28 text-center"
//                 />
//               </div>
//               <div className="w-[1px] h-6 bg-white/10" />
//               <div className="flex items-center gap-3">
//                 <span className="text-[10px] text-white/40 uppercase font-black tracking-tighter">TO</span>
//                 <DatePicker
//                   selected={toMonth ? new Date(toMonth + "-01") : null}
//                   onChange={(date) => {
//                     setToMonth(date ? moment(date).format("YYYY-MM") : "");
//                   }}
//                   dateFormat="MMMM, yyyy"
//                   showMonthYearPicker
//                   maxDate={moment().subtract(1, 'month').endOf('month').toDate()}
//                   className="bg-transparent border-none text-sm text-white font-bold focus:outline-none cursor-pointer w-28 text-center"
//                 />
//               </div>
//             </div>
//             {records.length > 0 && (
//               <div className="flex items-center gap-2">
//                 <button
//                   onClick={handleApprove}
//                   disabled={isApproving || isPayrollApproved}
//                   className="bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 border border-blue-500/30 px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition disabled:opacity-50"
//                 >
//                   {isApproving ? <span className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" /> : <CheckCircle2 size={16} />}
//                   Approve
//                 </button>
//                 <button
//                   onClick={handlePaid}
//                   disabled={isPaying || isPayrollPaid}
//                   className="bg-green-600/20 hover:bg-green-600/30 text-green-400 border border-green-500/30 px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition disabled:opacity-50"
//                 >
//                   {isPaying ? <span className="w-4 h-4 border-2 border-green-400 border-t-transparent rounded-full animate-spin" /> : <Banknote size={16} />}
//                   Mark Paid
//                 </button>
//               </div>
//             )}
//             <button
//               onClick={handleExport}
//               disabled={isExporting}
//               className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition border border-white/10 disabled:opacity-50"
//             >
//               {isExporting ? <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <Download size={16} />}
//               Export Report
//             </button>
//           </div>
//         </div>

//         {/* SUMMARY STATS */}
//         <PayrollKPIs fromMonth={fromMonth} toMonth={toMonth} refreshTrigger={refreshTrigger} />

//         {/* CONTROLS */}
//         <div className="bg-white/5 border border-white/10 p-4 rounded-2xl flex flex-col md:flex-row items-center justify-between gap-4">
//           <div className="relative w-full md:w-96">
//             <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/60" />
//             <input
//               type="text"
//               placeholder="Search by name or ID..."
//               value={searchTerm}
//               onChange={(e) => setSearchTerm(e.target.value)}
//               className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-purple-500/50 transition"
//             />
//           </div>

//           <div className="flex items-center gap-3 w-full md:w-auto">
//             <div className="flex bg-white/5 border border-white/10 rounded-lg p-1">
//               <button
//                 onClick={() => setViewType("list")}
//                 className={`p-2 rounded-md transition ${viewType === "list" ? "bg-purple-600 text-white" : "text-white/70 hover:text-white"}`}
//               >
//                 <List size={18} />
//               </button>
//               <button
//                 onClick={() => setViewType("grid")}
//                 className={`p-2 rounded-md transition ${viewType === "grid" ? "bg-purple-600 text-white" : "text-white/70 hover:text-white"}`}
//               >
//                 <LayoutGrid size={18} />
//               </button>
//             </div>
//             <button className="flex-1 md:flex-none bg-white/5 border border-white/10 px-4 py-2.5 rounded-xl flex items-center justify-center gap-2 hover:bg-white/10 transition text-sm">
//               <Filter size={18} /> Filters
//             </button>
//           </div>
//         </div>

//         {/* RECORDS LIST/GRID */}
//         {viewType === "list" ? (
//           <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden overflow-x-auto">
//             <table className="w-full text-left border-collapse min-w-[1000px]">
//               <thead>
//                 <tr className="border-b border-white/10 bg-white/5">
//                   <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider cursor-pointer group" onClick={() => handleSort('user_id')}>
//                     <div className="flex items-center gap-2">ID <ArrowUpDown size={12} className="opacity-0 group-hover:opacity-100 transition" /></div>
//                   </th>
//                   <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider cursor-pointer group" onClick={() => handleSort('NAME')}>
//                     <div className="flex items-center gap-2">Employee <ArrowUpDown size={12} className="opacity-0 group-hover:opacity-100 transition" /></div>
//                   </th>
//                   <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider text-right">Month</th>
//                   <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider text-right">Gross Salary</th>
//                   <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider text-right">Total Deductions</th>
//                   <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider text-right">Hold-back</th>
//                   <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider text-right">Net Salary</th>
//                   <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider text-center">Status</th>
//                   <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider text-center">Actions</th>
//                 </tr>
//               </thead>
//               <tbody className="divide-y divide-white/5">
//                 {currentRecords.map((rec) => (
//                   <tr key={rec.id} className="hover:bg-white/5 transition group">
//                     <td className="p-4 text-sm text-white/60">{rec.user_id}</td>
//                     <td className="p-4">
//                       <div className="flex items-center gap-3">
//                         <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-[12px] font-bold uppercase">
//                           {(rec.NAME || "U")[0]}
//                         </div>
//                         <div>
//                           <p className="text-sm font-semibold text-white">{rec.NAME}</p>
//                           {/* <p className="text-[10px] text-white/70">{rec.created_at}</p> */}
//                         </div>
//                       </div>
//                     </td>
//                     <td className="p-4 text-sm text-right text-white/60">{rec.month}</td>
//                     <td className="p-4 text-sm font-medium text-right">₹{Number(rec.gross_salary).toLocaleString()}</td>
//                     <td className="p-4 text-sm font-medium text-right text-red-400">₹{Number(rec.total_deduction).toLocaleString()}</td>
//                     <td className="p-4 text-sm font-medium text-right text-amber-400">-₹{Number(rec.hold_back_amount || 0).toLocaleString()}</td>
//                     <td className="p-4 text-right">
//                       <span className="text-sm font-black text-green-400">₹{Number(rec.net_salary).toLocaleString()}</span>
//                     </td>
//                     <td className="p-4 text-center">
//                       <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${rec.status === 'draft' ? 'bg-yellow-500/10 text-yellow-500 border border-yellow-500/20' :
//                         rec.status === 'approved' ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' :
//                           'bg-green-500/10 text-green-500 border border-green-500/20'
//                         }`}>
//                         {rec.status === 'draft' ? <Clock size={10} /> : <CheckCircle2 size={10} />}
//                         {rec.status}
//                       </span>
//                     </td>
//                     <td className="p-4">
//                       <div className="flex items-center justify-center gap-2">
//                         <button
//                           onClick={() => handleViewPayslip(rec.user_id)}
//                           disabled={isFetchingPayslip}
//                           className="p-2 hover:bg-white/10 rounded-lg text-white/70 hover:text-white transition disabled:opacity-50"
//                           title="View Payslip"
//                         >
//                           <FileText size={16} />
//                         </button>
//                         {/* <button className="p-2 hover:bg-white/10 rounded-lg text-white/70 hover:text-white transition" title="View Details">
//                           <Eye size={16} />
//                         </button> */}
//                       </div>
//                     </td>
//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           </div>
//         ) : (
//           <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
//             {currentRecords.map((rec) => {
//               const isMaturityMonth = Number(rec.net_salary) > (Number(rec.gross_salary) - Number(rec.total_deduction));
//               return (
//                 <div key={rec.id} className={`bg-white/5 border border-white/10 p-5 rounded-2xl hover:border-purple-500/50 transition space-y-4 group relative ${isMaturityMonth ? 'ring-2 ring-purple-500 shadow-[0_0_20px_rgba(168,85,247,0.4)]' : ''}`}>
//                   {isMaturityMonth && (
//                     <div className="absolute -top-3 -right-3 bg-purple-500 text-white text-[10px] font-black px-3 py-1 rounded-full shadow-lg animate-bounce z-10">
//                       MATURITY RELEASED!
//                     </div>
//                   )}
//                   <div className="flex items-center justify-between">
//                     <div className="flex items-center gap-3">
//                       <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center font-bold border-2 border-white/10 uppercase">
//                         {(rec.NAME || "U")[0]}
//                       </div>
//                       <div>
//                         <h4 className="text-sm font-bold text-white">{rec.NAME}</h4>
//                         <p className="text-[10px] text-white/70">ID: {rec.user_id}</p>
//                       </div>
//                     </div>
//                     <span className={`px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest ${rec.status === 'draft' ? 'bg-yellow-500/20 text-yellow-500 border border-yellow-500/20' :
//                       rec.status === 'approved' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/20' :
//                         'bg-green-500/20 text-green-400 border border-green-500/20'
//                       }`}>
//                       {rec.status}
//                     </span>
//                   </div>

//                   <div className="space-y-2 py-3 border-y border-white/5">
//                     <DataRow label="Gross Salary" value={`₹${Number(rec.gross_salary).toLocaleString()}`} />
//                     <DataRow label="Deductions" value={`-₹${Number(rec.total_deduction).toLocaleString()}`} color="text-red-400" />
//                     <DataRow label="Hold-back" value={`-₹${Number(rec.hold_back_amount || 0).toLocaleString()}`} color="text-amber-400" />
//                     {isMaturityMonth && (
//                       <div className="flex justify-between text-[11px] bg-purple-500/10 p-1 rounded">
//                         <span className="text-purple-300 font-bold">Released Amount</span>
//                         <span className="font-black text-purple-300">+₹{(Number(rec.net_salary) - (Number(rec.gross_salary) - Number(rec.total_deduction) - Number(rec.hold_back_amount || 0))).toLocaleString()}</span>
//                       </div>
//                     )}
//                     <DataRow label="Month" value={rec.month} />
//                   </div>

//                   <div className="flex items-center justify-between pt-1">
//                     <div>
//                       <p className="text-[10px] text-white/60 uppercase font-bold tracking-wider">Net Payable</p>
//                       <p className="text-xl font-black text-green-400">₹{Number(rec.net_salary).toLocaleString()}</p>
//                     </div>
//                     <div className="flex gap-1">
//                       <button
//                         onClick={() => handleViewPayslip(rec.user_id)}
//                         disabled={isFetchingPayslip}
//                         className="bg-white/5 hover:bg-white/10 p-2.5 rounded-xl transition border border-white/5 disabled:opacity-50"
//                         title="View Payslip"
//                       >
//                         <FileText size={16} className="text-white/60" />
//                       </button>
//                       {/* <button className="bg-white/5 hover:bg-white/10 p-2.5 rounded-xl transition border border-white/5">
//                       <Eye size={16} className="text-white/60" />
//                     </button> */}
//                     </div>
//                   </div>
//                 </div>
//               )
//             })}
//           </div>
//         )}

//         {/* PAGINATION */}
//         {filteredRecords.length > recordsPerPage && (
//           <div className="flex items-center justify-center gap-2 py-6">
//             <button
//               onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
//               disabled={currentPage === 1}
//               className="p-2 text-white/70 hover:text-white transition disabled:opacity-30"
//             >
//               <ChevronRight size={18} className="rotate-180" />
//             </button>

//             {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
//               <button
//                 key={page}
//                 onClick={() => setCurrentPage(page)}
//                 className={`w-8 h-8 rounded-lg text-sm font-bold transition ${currentPage === page
//                   ? 'bg-purple-600 text-white'
//                   : 'bg-white/5 text-white/60 hover:bg-white/10'
//                   }`}
//               >
//                 {page}
//               </button>
//             ))}

//             <button
//               onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
//               disabled={currentPage === totalPages}
//               className="p-2 text-white/70 hover:text-white transition disabled:opacity-30"
//             >
//               <ChevronRight size={18} />
//             </button>
//           </div>
//         )}

//         {filteredRecords.length === 0 && (
//           <div className="bg-white/5 border border-white/10 rounded-2xl p-12 text-center">
//             <div className="bg-white/5 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
//               <History size={32} className="text-white/50" />
//             </div>
//             <h3 className="text-lg font-bold text-white">No payroll records found</h3>
//             <p className="text-white/70 text-sm mt-1">Try selecting a different month or processing payroll first</p>
//           </div>
//         )}

//         <PayslipModal
//           isOpen={isPayslipModalOpen}
//           onClose={() => setIsPayslipModalOpen(false)}
//           data={payslipData}
//         />
//       </div>
//     </div>
//   );
// };

// const DataRow = ({ label, value, color = "text-white" }) => (
//   <div className="flex justify-between items-center text-[11px]">
//     <span className="text-white/70 font-medium">{label}</span>
//     <span className={`${color} font-bold`}>{value}</span>
//   </div>
// );

// export default PayrollRecords;






// // import React, { useState, useEffect } from "react";
// // import {
// //   History,
// //   Search,
// //   Download,
// //   Filter,
// //   Eye,
// //   ChevronRight,
// //   Calendar,
// //   LayoutGrid,
// //   List,
// //   ArrowUpDown,
// //   CheckCircle2,
// //   Clock,
// //   AlertCircle,
// //   FileText,
// //   Banknote
// // } from "lucide-react";
// // import { getPayrollRecordsApi, approvePayrollApi, markPayrollPaidApi, exportPayrollReportApi, getPayslipApi } from "../../app/api";
// // import PayrollKPIs from "./components/PayrollKPIs";
// // import PayslipModal from "./components/PayslipModal";
// // import { toast } from "react-hot-toast";
// // import DatePicker from "react-datepicker";
// // import "react-datepicker/dist/react-datepicker.css";
// // import moment from "moment";

// // const PayrollRecords = () => {
// //   const currentMonth = new Date().toISOString().slice(0, 7);
// //   const [fromMonth, setFromMonth] = useState(currentMonth); // YYYY-MM
// //   const [toMonth, setToMonth] = useState(currentMonth); // YYYY-MM
// //   const [loading, setLoading] = useState(true);
// //   const [records, setRecords] = useState([]);
// //   const [viewType, setViewType] = useState("list");
// //   const [searchTerm, setSearchTerm] = useState("");
// //   const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
// //   const [isApproving, setIsApproving] = useState(false);
// //   const [isPaying, setIsPaying] = useState(false);
// //   const [isExporting, setIsExporting] = useState(false);
// //   const [refreshTrigger, setRefreshTrigger] = useState(0);

// //   const [payslipData, setPayslipData] = useState(null);
// //   const [isPayslipModalOpen, setIsPayslipModalOpen] = useState(false);
// //   const [isFetchingPayslip, setIsFetchingPayslip] = useState(false);

// //   useEffect(() => {
// //     fetchRecords();
// //   }, [fromMonth, toMonth, refreshTrigger]);

// //   const fetchRecords = async () => {
// //     try {
// //       setLoading(true);
// //       const res = await getPayrollRecordsApi(fromMonth, toMonth);
// //       if (res.success) {
// //         setRecords(res.data);
// //       } else {
// //         toast.error(res.message || "Failed to fetch payroll records");
// //       }
// //     } catch (error) {
// //       console.error("Error fetching records:", error);
// //       toast.error("An error occurred while fetching records");
// //     } finally {
// //       setLoading(false);
// //     }
// //   };

// //   const handleApprove = async () => {
// //     try {
// //       setIsApproving(true);
// //       const res = await approvePayrollApi({ month: fromMonth });
// //       if (res.success) {
// //         toast.success("Payroll approved successfully!");
// //         setRefreshTrigger(prev => prev + 1);
// //       }
// //     } catch (error) {
// //       toast.error("Failed to approve payroll");
// //     } finally {
// //       setIsApproving(false);
// //     }
// //   };

// //   const handlePaid = async () => {
// //     try {
// //       setIsPaying(true);
// //       const res = await markPayrollPaidApi({ month: fromMonth });
// //       if (res.success) {
// //         toast.success("Payroll marked as paid!");
// //         setRefreshTrigger(prev => prev + 1);
// //       }
// //     } catch (error) {
// //       toast.error("Failed to mark as paid");
// //     } finally {
// //       setIsPaying(false);
// //     }
// //   };

// //   const handleExport = async () => {
// //     try {
// //       setIsExporting(true);
// //       await exportPayrollReportApi(fromMonth, toMonth);
// //       toast.success("Report downloaded successfully!");
// //     } catch (error) {
// //       toast.error("Failed to export report");
// //     } finally {
// //       setIsExporting(false);
// //     }
// //   };

// //   const handleViewPayslip = async (userId) => {
// //     try {
// //       setIsFetchingPayslip(true);

// //       // fromMonth is YYYY-MM (e.g. 2026-05)
// //       // API expects userId, month (name), year (string)
// //       const dateObj = moment(fromMonth, "YYYY-MM");
// //       const monthName = dateObj.format("MMMM"); // "May"
// //       const yearStr = dateObj.format("YYYY");   // "2026"

// //       const res = await getPayslipApi(userId, monthName, yearStr);
// //       if (res.success) {
// //         setPayslipData(res.data);
// //         setIsPayslipModalOpen(true);
// //       } else {
// //         toast.error("Failed to load payslip data");
// //       }
// //     } catch (error) {
// //       toast.error("Error loading payslip");
// //     } finally {
// //       setIsFetchingPayslip(false);
// //     }
// //   };

// //   const handleSort = (key) => {
// //     let direction = 'asc';
// //     if (sortConfig.key === key && sortConfig.direction === 'asc') {
// //       direction = 'desc';
// //     }
// //     setSortConfig({ key, direction });
// //   };

// //   const sortedRecords = [...records].sort((a, b) => {
// //     if (a[sortConfig.key] < b[sortConfig.key]) {
// //       return sortConfig.direction === 'asc' ? -1 : 1;
// //     }
// //     if (a[sortConfig.key] > b[sortConfig.key]) {
// //       return sortConfig.direction === 'asc' ? 1 : -1;
// //     }
// //     return 0;
// //   });

// //   const filteredRecords = sortedRecords.filter(rec =>
// //     rec.NAME?.toLowerCase().includes(searchTerm.toLowerCase()) ||
// //     rec.user_id?.toString().includes(searchTerm)
// //   );

// //   const isPayrollApproved = records.some(r => r.status === "approved" || r.status === "paid");
// //   const isPayrollPaid = records.some(r => r.status === "paid");

// //   if (loading) {
// //     return (
// //       <div className="min-h-screen bg-[#12082F] flex items-center justify-center">
// //         <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
// //       </div>
// //     );
// //   }

// //   return (
// //     <div className="min-h-screen bg-[#12082F] text-white p-4 sm:p-6 font-sans">
// //       <div className="max-w-[1600px] mx-auto space-y-6">

// //         {/* HEADER */}
// //         <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
// //           <div>
// //             <h1 className="text-2xl font-bold flex items-center gap-2 text-white">
// //               PAYROLL RECORDS <span className="text-white/70 font-normal">| {moment(fromMonth).format("MMMM YYYY")} {toMonth && `- ${moment(toMonth).format("MMMM YYYY")}`}</span>
// //             </h1>
// //             <p className="text-white/50 text-sm">Historical payroll data and processing logs</p>
// //           </div>
// //           <div className="flex flex-wrap items-center gap-3">
// //             <div className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-2 shadow-inner">
// //               <div className="flex items-center gap-3">
// //                 <span className="text-[10px] text-white/40 uppercase font-black tracking-tighter">FROM</span>
// //                 <DatePicker
// //                   selected={fromMonth ? new Date(fromMonth + "-01") : null}
// //                   onChange={(date) => {
// //                     setFromMonth(date ? moment(date).format("YYYY-MM") : "");
// //                   }}
// //                   dateFormat="MMMM, yyyy"
// //                   showMonthYearPicker
// //                   className="bg-transparent border-none text-sm text-white font-bold focus:outline-none cursor-pointer w-28 text-center"
// //                 />
// //               </div>
// //               <div className="w-[1px] h-6 bg-white/10" />
// //               <div className="flex items-center gap-3">
// //                 <span className="text-[10px] text-white/40 uppercase font-black tracking-tighter">TO</span>
// //                 <DatePicker
// //                   selected={toMonth ? new Date(toMonth + "-01") : null}
// //                   onChange={(date) => {
// //                     setToMonth(date ? moment(date).format("YYYY-MM") : "");
// //                   }}
// //                   dateFormat="MMMM, yyyy"
// //                   showMonthYearPicker
// //                   className="bg-transparent border-none text-sm text-white font-bold focus:outline-none cursor-pointer w-28 text-center"
// //                 />
// //               </div>
// //             </div>
// //             {records.length > 0 && (
// //               <div className="flex items-center gap-2">
// //                 <button
// //                   onClick={handleApprove}
// //                   disabled={isApproving || isPayrollApproved}
// //                   className="bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 border border-blue-500/30 px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition disabled:opacity-50"
// //                 >
// //                   {isApproving ? <span className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" /> : <CheckCircle2 size={16} />}
// //                   Approve
// //                 </button>
// //                 <button
// //                   onClick={handlePaid}
// //                   disabled={isPaying || isPayrollPaid}
// //                   className="bg-green-600/20 hover:bg-green-600/30 text-green-400 border border-green-500/30 px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition disabled:opacity-50"
// //                 >
// //                   {isPaying ? <span className="w-4 h-4 border-2 border-green-400 border-t-transparent rounded-full animate-spin" /> : <Banknote size={16} />}
// //                   Mark Paid
// //                 </button>
// //               </div>
// //             )}
// //             <button
// //               onClick={handleExport}
// //               disabled={isExporting}
// //               className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition border border-white/10 disabled:opacity-50"
// //             >
// //               {isExporting ? <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <Download size={16} />}
// //               Export Report
// //             </button>
// //           </div>
// //         </div>

// //         {/* SUMMARY STATS */}
// //         <PayrollKPIs fromMonth={fromMonth} toMonth={toMonth} refreshTrigger={refreshTrigger} />

// //         {/* CONTROLS */}
// //         <div className="bg-white/5 border border-white/10 p-4 rounded-2xl flex flex-col md:flex-row items-center justify-between gap-4">
// //           <div className="relative w-full md:w-96">
// //             <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/60" />
// //             <input
// //               type="text"
// //               placeholder="Search by name or ID..."
// //               value={searchTerm}
// //               onChange={(e) => setSearchTerm(e.target.value)}
// //               className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-purple-500/50 transition"
// //             />
// //           </div>

// //           <div className="flex items-center gap-3 w-full md:w-auto">
// //             <div className="flex bg-white/5 border border-white/10 rounded-lg p-1">
// //               <button
// //                 onClick={() => setViewType("list")}
// //                 className={`p-2 rounded-md transition ${viewType === "list" ? "bg-purple-600 text-white" : "text-white/70 hover:text-white"}`}
// //               >
// //                 <List size={18} />
// //               </button>
// //               <button
// //                 onClick={() => setViewType("grid")}
// //                 className={`p-2 rounded-md transition ${viewType === "grid" ? "bg-purple-600 text-white" : "text-white/70 hover:text-white"}`}
// //               >
// //                 <LayoutGrid size={18} />
// //               </button>
// //             </div>
// //             <button className="flex-1 md:flex-none bg-white/5 border border-white/10 px-4 py-2.5 rounded-xl flex items-center justify-center gap-2 hover:bg-white/10 transition text-sm">
// //               <Filter size={18} /> Filters
// //             </button>
// //           </div>
// //         </div>

// //         {/* RECORDS LIST/GRID */}
// //         {viewType === "list" ? (
// //           <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden overflow-x-auto">
// //             <table className="w-full text-left border-collapse min-w-[1000px]">
// //               <thead>
// //                 <tr className="border-b border-white/10 bg-white/5">
// //                   <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider cursor-pointer group" onClick={() => handleSort('user_id')}>
// //                     <div className="flex items-center gap-2">ID <ArrowUpDown size={12} className="opacity-0 group-hover:opacity-100 transition" /></div>
// //                   </th>
// //                   <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider cursor-pointer group" onClick={() => handleSort('name')}>
// //                     <div className="flex items-center gap-2">Employee <ArrowUpDown size={12} className="opacity-0 group-hover:opacity-100 transition" /></div>
// //                   </th>
// //                   <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider text-right">Month</th>
// //                   <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider text-right">Gross Salary</th>
// //                   <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider text-right">Total Deductions</th>
// //                   <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider text-right">Net Salary</th>
// //                   <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider text-center">Status</th>
// //                   <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider text-center">Actions</th>
// //                 </tr>
// //               </thead>
// //               <tbody className="divide-y divide-white/5">
// //                 {filteredRecords.map((rec) => (
// //                   <tr key={rec.id} className="hover:bg-white/5 transition group">
// //                     <td className="p-4 text-sm text-white/60">{rec.user_id}</td>
// //                     <td className="p-4">
// //                       <div className="flex items-center gap-3">
// //                         <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-[12px] font-bold uppercase">
// //                           {(rec.NAME || "U")[0]}
// //                         </div>
// //                         <div>
// //                           <p className="text-sm font-semibold text-white">{rec.NAME}</p>
// //                           {/* <p className="text-[10px] text-white/70">{rec.created_at}</p> */}
// //                         </div>
// //                       </div>
// //                     </td>
// //                     <td className="p-4 text-sm text-right text-white/60">{rec.month}</td>
// //                     <td className="p-4 text-sm font-medium text-right">₹{Number(rec.gross_salary).toLocaleString()}</td>
// //                     <td className="p-4 text-sm font-medium text-right text-red-400">₹{Number(rec.total_deduction).toLocaleString()}</td>
// //                     <td className="p-4 text-right">
// //                       <span className="text-sm font-black text-green-400">₹{Number(rec.net_salary).toLocaleString()}</span>
// //                     </td>
// //                     <td className="p-4 text-center">
// //                       <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${rec.status === 'draft' ? 'bg-yellow-500/10 text-yellow-500 border border-yellow-500/20' :
// //                         rec.status === 'approved' ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' :
// //                           'bg-green-500/10 text-green-500 border border-green-500/20'
// //                         }`}>
// //                         {rec.status === 'draft' ? <Clock size={10} /> : <CheckCircle2 size={10} />}
// //                         {rec.status}
// //                       </span>
// //                     </td>
// //                     <td className="p-4">
// //                       <div className="flex items-center justify-center gap-2">
// //                         <button
// //                           onClick={() => handleViewPayslip(rec.user_id)}
// //                           disabled={isFetchingPayslip}
// //                           className="p-2 hover:bg-white/10 rounded-lg text-white/70 hover:text-white transition disabled:opacity-50"
// //                           title="View Payslip"
// //                         >
// //                           <FileText size={16} />
// //                         </button>
// //                         {/* <button className="p-2 hover:bg-white/10 rounded-lg text-white/70 hover:text-white transition" title="View Details">
// //                           <Eye size={16} />
// //                         </button> */}
// //                       </div>
// //                     </td>
// //                   </tr>
// //                 ))}
// //               </tbody>
// //             </table>
// //           </div>
// //         ) : (
// //           <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
// //             {filteredRecords.map((rec) => (
// //               <div key={rec.id} className="bg-white/5 border border-white/10 p-5 rounded-2xl hover:border-purple-500/50 transition space-y-4 group">
// //                 <div className="flex items-center justify-between">
// //                   <div className="flex items-center gap-3">
// //                     <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center font-bold border-2 border-white/10 uppercase">
// //                       {(rec.NAME || "U")[0]}
// //                     </div>
// //                     <div>
// //                       <h4 className="text-sm font-bold text-white">{rec.NAME}</h4>
// //                       <p className="text-[10px] text-white/70">ID: {rec.user_id}</p>
// //                     </div>
// //                   </div>
// //                   <span className={`px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest ${rec.status === 'draft' ? 'bg-yellow-500/20 text-yellow-500 border border-yellow-500/20' :
// //                     rec.status === 'approved' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/20' :
// //                       'bg-green-500/20 text-green-400 border border-green-500/20'
// //                     }`}>
// //                     {rec.status}
// //                   </span>
// //                 </div>

// //                 <div className="space-y-2 py-3 border-y border-white/5">
// //                   <DataRow label="Gross Salary" value={`₹${Number(rec.gross_salary).toLocaleString()}`} />
// //                   <DataRow label="Deductions" value={`-₹${Number(rec.total_deduction).toLocaleString()}`} color="text-red-400" />
// //                   <DataRow label="Month" value={rec.month} />
// //                 </div>

// //                 <div className="flex items-center justify-between pt-1">
// //                   <div>
// //                     <p className="text-[10px] text-white/60 uppercase font-bold tracking-wider">Net Payable</p>
// //                     <p className="text-xl font-black text-green-400">₹{Number(rec.net_salary).toLocaleString()}</p>
// //                   </div>
// //                   <div className="flex gap-1">
// //                     <button
// //                       onClick={() => handleViewPayslip(rec.user_id)}
// //                       disabled={isFetchingPayslip}
// //                       className="bg-white/5 hover:bg-white/10 p-2.5 rounded-xl transition border border-white/5 disabled:opacity-50"
// //                       title="View Payslip"
// //                     >
// //                       <FileText size={16} className="text-white/60" />
// //                     </button>
// //                     {/* <button className="bg-white/5 hover:bg-white/10 p-2.5 rounded-xl transition border border-white/5">
// //                       <Eye size={16} className="text-white/60" />
// //                     </button> */}
// //                   </div>
// //                 </div>
// //               </div>
// //             ))}
// //           </div>
// //         )}

// //         {filteredRecords.length === 0 && (
// //           <div className="bg-white/5 border border-white/10 rounded-2xl p-12 text-center">
// //             <div className="bg-white/5 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
// //               <History size={32} className="text-white/50" />
// //             </div>
// //             <h3 className="text-lg font-bold text-white">No payroll records found</h3>
// //             <p className="text-white/70 text-sm mt-1">Try selecting a different month or processing payroll first</p>
// //           </div>
// //         )}

// //         <PayslipModal
// //           isOpen={isPayslipModalOpen}
// //           onClose={() => setIsPayslipModalOpen(false)}
// //           data={payslipData}
// //         />
// //       </div>
// //     </div>
// //   );
// // };

// // const DataRow = ({ label, value, color = "text-white" }) => (
// //   <div className="flex justify-between items-center text-[11px]">
// //     <span className="text-white/70 font-medium">{label}</span>
// //     <span className={`${color} font-bold`}>{value}</span>
// //   </div>
// // );

// // export default PayrollRecords;
